// src/services/appGuideService.js
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { uploadLibraryFile } from './libraryService';

const APP_GUIDE_COLLECTION = 'app_guide';

/**
 * Real-time listener for app guide steps ordered by 'order'
 */
export const subscribeToGuideSteps = (callback) => {
  const q = query(
    collection(db, APP_GUIDE_COLLECTION),
    orderBy('order', 'asc')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const steps = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      callback(steps);
    },
    (error) => {
      console.error('[AppGuideService] Subscription error:', error);
      callback([]);
    }
  );
};

/**
 * Fetch all guide steps once
 */
export const fetchGuideSteps = async () => {
  try {
    const q = query(
      collection(db, APP_GUIDE_COLLECTION),
      orderBy('order', 'asc')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('[AppGuideService] Error fetching guide steps:', error);
    return [];
  }
};

/**
 * Upload a guide screenshot to Cloudinary
 */
export const uploadGuideScreenshot = async (file, onProgress) => {
  return uploadLibraryFile(file, 'smartbrain_app_guide', onProgress);
};

/**
 * Save or update a guide step
 */
export const saveGuideStep = async (stepData) => {
  try {
    const isEdit = !!stepData.id;
    const docId = isEdit ? stepData.id : `step_${Date.now()}`;
    const docRef = doc(db, APP_GUIDE_COLLECTION, docId);

    const payload = {
      order: Number(stepData.order || 1),
      title: stepData.title?.trim() || 'Untitled Step',
      description: stepData.description?.trim() || '',
      imageUrl: stepData.imageUrl || '',
      isActive: stepData.isActive !== undefined ? stepData.isActive : true,
      updatedAt: serverTimestamp(),
    };

    if (!isEdit) {
      payload.createdAt = serverTimestamp();
    }

    await setDoc(docRef, payload, { merge: true });
    return docId;
  } catch (error) {
    console.error('[AppGuideService] Error saving guide step:', error);
    throw error;
  }
};

/**
 * Delete a guide step
 */
export const deleteGuideStep = async (stepId) => {
  try {
    await deleteDoc(doc(db, APP_GUIDE_COLLECTION, stepId));
  } catch (error) {
    console.error('[AppGuideService] Error deleting guide step:', error);
    throw error;
  }
};

/**
 * Seed initial default guide steps for first-time setup
 */
export const seedDefaultGuideSteps = async () => {
  const defaultSteps = [
    {
      order: 1,
      title: 'Welcome to SmartBrain App',
      description:
        'SmartBrain is your all-in-one Nigerian secondary school learning companion! Choose your class level (JSS 1-3, SS 1-3, or UTME prep) and get instant access to full syllabus topics, CBT tests, and AI tutoring.',
      imageUrl: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop&q=80',
      isActive: true,
    },
    {
      order: 2,
      title: 'Learn with Teacher Zikora (AI Tutor)',
      description:
        'Tap the AI Tutor icon at any time to ask questions on difficult topics in Mathematics, English, Sciences, and Commercial subjects. Teacher Zikora explains step-by-step with authentic everyday Nigerian examples!',
      imageUrl: 'https://images.unsplash.com/photo-1577896851231-70ef18881754?w=800&auto=format&fit=crop&q=80',
      isActive: true,
    },
    {
      order: 3,
      title: 'Practice with Real CBT Quizzes',
      description:
        'Test your speed and mastery with timed CBT practice quizzes based on curriculum standards. Get instant score breakdowns, clear explanations for every wrong answer, and track your progress.',
      imageUrl: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&auto=format&fit=crop&q=80',
      isActive: true,
    },
    {
      order: 4,
      title: 'Track Your Mastery & Weak Topics',
      description:
        'The Progress tab monitors your daily study streaks, subject mastery percentages, and automatically highlights weak areas you need to review before school or WAEC/NECO exams.',
      imageUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop&q=80',
      isActive: true,
    },
  ];

  for (const step of defaultSteps) {
    await saveGuideStep(step);
  }
};
